<template>
    <div class="modal-container">
        <form-wrapper class="modal-form" :validator="$v.rateDetail">
            <header class="modal-header">
                <div class="modal-header__right">
                    <md-button class="md-simple md-just-icon md-round" @click.stop="$modal.hide">
                        <md-icon>clear</md-icon>
                    </md-button>
                </div>
            </header>

            <section class="modal-section">
                <div class="section-override">
                    <md-switch v-model="rateDetail.overrideRule">
                       Override
                    </md-switch>
                </div>
                <div class="section-heading">
                    <span class="section-heading__badge">Timeframe</span>
                    <p class="section-heading__copy">Choose when this surcharge is active.</p>
                </div>
                <div class="section-card section-card--wide">
                    <rule-valid-days
                        :current-days="rateDetail.validForWeekDays"
                        :from-date="rateDetail.validFromDate"
                        :to-date="rateDetail.validToDate"
                        @daysSelected="handleDaysSeleceted"
                        ref="ruleValidDays"
                    />
                </div>
            </section>

            <section class="modal-section">
                <div class="section-heading">
                    <span class="section-heading__badge">Details</span>
                    <p class="section-heading__copy">Name the surcharge and define how it is calculated.</p>
                </div>
                <div class="section-card section-card--compact">
                    <div class="charging-grid">
                        <div class="charging-group">
                            <h5 class="charging-group__title">Descriptor</h5>
                            <p class="charging-group__hint">Visible name when the surcharge is applied.</p>
                            <div class="field-pair">
                                <form-group name="surchargeName" label="Name">
                                    <md-input v-model.trim="rateDetail.surchargeName" />
                                </form-group>
                            </div>
                        </div>

                        <div class="charging-separator"></div>

                        <div class="charging-group">
                            <h5 class="charging-group__title">Amount</h5>
                            <p class="charging-group__hint">Specify percentage or flat rate.</p>
                            <div class="field-pair">
                                <form-group name="surchargePercentage" label="Percentage">
                                    <rule-operator
                                        v-if="$_nullChecker(rateDetail.surchargePercentage)"
                                        v-model="rateDetail.surchargePercentage.operator"
                                        :value="rateDetail.surchargePercentage.operator"
                                    />
                                    <md-input v-model.number="rateDetail.surchargePercentage.value" />
                                </form-group>
                                <form-group name="surchargeFlatRate" label="Flat Rate">
                                    <rule-operator
                                        v-if="$_nullChecker(rateDetail.surchargeFlatRate)"
                                        v-model="rateDetail.surchargeFlatRate.operator"
                                        :value="rateDetail.surchargeFlatRate.operator"
                                    />
                                    <md-input type="number" v-model.number="rateDetail.surchargeFlatRate.value" />
                                </form-group>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <footer class="modal-footer">
                <div class="modal-footer__actions">
                    <md-button class="md-default dialog-button" @click.stop="$modal.hide">Cancel</md-button>
                    <md-button class="md-primary dialog-button" @click="create()">
                        {{ isUpdate ? 'Update' : 'Create' }}
                    </md-button>
                </div>
            </footer>
        </form-wrapper>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { minLength, maxLength, required, decimal } from 'vuelidate/lib/validators';
import { RATE_RULE } from '@/utils/defaults';
import { GeneralMixin, RatesEngineMixin } from '@/mixins';
import { handleRequests, showErrorMessage } from '@/helpers';
import { RATE_CHARGING_TYPES } from '@/utils/constants';
import { RuleValidDays } from '@/components';
import RuleOperator from './RuleOperator';

export default {
    name: 'CreateSurchargeRuleContent',
    components: {
        RuleValidDays,
        RuleOperator
    },
    mixins: [GeneralMixin, RatesEngineMixin],
    props: {
        rateDetail: {
            type: Object,
            default: () => Object.assign({}, RATE_RULE()),
        },
        resolve: {
            type: Function,
            default: () => {}
        },
        listLength: {
            type: Number,
            default: 0
        },
        isUpdate: {
            type: Boolean,
            default: false
        },
        customerId: {
            type: Number,
            default: null
        },
        rateGroupId: {
            type: Number,
            default: null
        },
        driverUserId: {
            type: Number,
            default: null
        },
        driverRuleCreation: {
            type: Boolean,
            default: false
        },
        teamRegionId: {
            type: Number,
            default: null
        }
    },
    created() {
        this.$_ensureChargeField('surchargePercentage');
        this.$_ensureChargeField('surchargeFlatRate');
    },
    data() {
        return {
            ruleData: {
                type: null,
                rule: 'test',
                customerId: null
            },
            rateChargingTypes: RATE_CHARGING_TYPES
        };
    },
    validations: {
        rateDetail: {
            surchargeName: {
                minLength: minLength(1),
                maxLength: maxLength(100),
                required
            },
            surchargePercentage: {
                value: {
                    decimal
                }
            },
            surchargeFlatRate: {
                value: {
                    decimal
                }
            }
        }
    },
    computed: {
        ...mapGetters({
            user: 'user/user'
        })
    },
    methods: {
        validateData() {
            if (this.$v.$invalid) {
                return false;
            }

            const surchargeFlatRate = this.$v.rateDetail.surchargeFlatRate.value.$model;
            const surchargePercentage = this.$v.rateDetail.surchargePercentage.value.$model;
            const surchargeName = this.$v.rateDetail.surchargeName.$model;

            if (surchargeName === null || surchargeName === undefined || surchargeName.trim() === '') {
                return false;
            }

            if (surchargeFlatRate === null && surchargePercentage === null) {
                return false;
            }

            return true;
        },
        async create() {
            const ruleDaysValid = this.$refs.ruleValidDays.validate();
            if (!ruleDaysValid) {
                return;
            }

            if (this.isUpdate) {
                this.updateRuleDetails();
                return;
            }

            if (!this.validateData()) {
                showErrorMessage(this, 'Rule must have valid data', null);
                return;
            }

            if (
                this.$v.rateDetail.surchargeFlatRate.value.$model === '' ||
                this.$v.rateDetail.surchargeFlatRate.value.$model === null ||
                this.$v.rateDetail.surchargeFlatRate.value.$model === undefined
            ) {
                this.rateDetail.surchargeFlatRateEnabled = false;
                this.rateDetail.surchargeEnabled = true;
            } else {
                this.rateDetail.surchargeFlatRateEnabled = true;
                this.rateDetail.surchargeEnabled = false;
            }

            this.$_handleLoaderState(true, 'PROCESSING...');

            const chargeType = this.rateChargingTypes.find((x) => x.key.toLowerCase() === this.$route.params.type);
            const obj = this.$_getRateRuleData(
                chargeType,
                this.user.publicUserId,
                this.rateDetail,
                this.customerId,
                this.listLength,
                this.rateGroupId,
                this.driverRuleCreation,
                this.driverUserId,
                this.teamRegionId
            );

            const endpoint = '/api/rates/create';
            const payload = {
                method: 'post',
                data: obj
            };

            try {
                const response = await handleRequests(endpoint, payload);

                if (response.status === 200) {
                    this.$notify({
                        message: 'Successfully created rule.',
                        type: 'success'
                    });

                    this.resolve({
                        record: response.data
                    });
                } else {
                    showErrorMessage(this, 'Failed creating rule.', null);
                }
            } catch (error) {
                showErrorMessage(this, error.data, null);
            }

            this.$_handleLoaderState(false);
        },
        async updateRuleDetails() {
            if (!this.validateData()) {
                showErrorMessage(this, 'Rule must have valid data', null);
                return;
            }

            this.$_handleLoaderState(true, 'UPDATING...');

            if (
                this.$v.rateDetail.surchargeFlatRate.$model === '' ||
                this.$v.rateDetail.surchargeFlatRate.$model === null ||
                this.$v.rateDetail.surchargeFlatRate.$model === undefined
            ) {
                this.rateDetail.surchargeFlatRateEnabled = false;
                this.rateDetail.surchargeEnabled = true;
            } else {
                this.rateDetail.surchargeFlatRateEnabled = true;
                this.rateDetail.surchargeEnabled = false;
            }

            const rateId = this.rateDetail.rateRulesId;
            this.rateDetail.condition = null;
            this.rateDetail.currency = null;
            this.rateDetail.priority = null;
            this.rateDetail.rateRulesId = null;

            const obj = {
                rateRulesId: rateId,
                rule: JSON.stringify(this.rateDetail)
            };

            const endpoint = '/api/rates/updateRate';
            const payload = {
                method: 'post',
                data: obj
            };

            try {
                const response = await handleRequests(endpoint, payload);

                if (response.status === 200) {
                    this.$notify({
                        message: 'Successfully updated rule.',
                        type: 'success'
                    });

                    this.resolve({
                        record: response.status
                    });
                } else {
                    showErrorMessage(this, 'Failed updating rule.', null);
                }
            } catch (error) {
                showErrorMessage(this, 'Failed updating rule.', null);
            }

            this.$_handleLoaderState(false);
        },
        handleDaysSeleceted(value) {
            this.rateDetail = Object.assign(this.rateDetail, value);
        }
    }
};
</script>

<style lang="scss" scoped>
@import './create-rule-content.shared.scss';
</style>
